"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { generateTenantCSS } from '@/lib/tenant';

// 🔒 ISOLAMENTO MULTI-TENANT: Cache isolado por subdomain
const getTenantCache = () => {
  if (typeof window === 'undefined') return new Map();
  if (!window.__tenantCaches) {
    window.__tenantCaches = new Map();
  }
  return window.__tenantCaches;
};

type TenantCacheEntry = {
  data: TenantConfig | null;
  timestamp: number;
  promise?: Promise<TenantConfig>;
  loading: boolean;
};

declare global {
  interface Window {
    __tenantCaches?: Map<string, TenantCacheEntry>;
  }
}

// 🔒 ISOLAMENTO: Limpar cache específico do tenant
export function clearTenantCache(subdomain: string) {
  if (typeof window === 'undefined') return;
  const cache = getTenantCache();
  cache.delete(subdomain);
  if (process.env.NODE_ENV === 'development') {
    console.log(`🗑️ Cache do tenant ${subdomain} limpo`);
  }
}

// 🔒 ISOLAMENTO: Limpar todo o cache (apenas para debug)
export function clearAllTenantCaches() {
  if (typeof window === 'undefined') return;
  const cache = getTenantCache();
  cache.clear();
  if (process.env.NODE_ENV === 'development') {
    console.log('🗑️ Todos os caches de tenants limpos');
  }
}

// 🔧 NOVA: Função para limpar CSS de tenant específico
export function clearTenantCSS(subdomain: string) {
  if (typeof window === 'undefined') return;
  const styleId = `__tenant_styles_${subdomain}`;
  const styleElement = document.getElementById(styleId);
  if (styleElement) {
    styleElement.remove();
    if (process.env.NODE_ENV === 'development') {
      console.log(`🗑️ CSS do tenant ${subdomain} removido`);
    }
  }
}

// 🔧 NOVA: Função para limpar todos os CSS de tenants
export function clearAllTenantCSS() {
  if (typeof window === 'undefined') return;
  const existingStyles = document.querySelectorAll('[id^="__tenant_styles_"]');
  existingStyles.forEach(style => {
    style.remove();
    if (process.env.NODE_ENV === 'development') {
      console.log(`🗑️ CSS removido: ${style.id}`);
    }
  });
  if (process.env.NODE_ENV === 'development') {
    console.log('🗑️ Todos os CSS de tenants limpos');
  }
}

// 🔧 NOVA: Função para aplicar CSS imediatamente
export function applyTenantCSS(tenant: TenantConfig, subdomain: string) {
  if (typeof window === 'undefined' || !tenant?.configurations) return;
  
  const styleId = `__tenant_styles_${subdomain}`;
  let styleElement = document.getElementById(styleId) as HTMLStyleElement;
  
  if (!styleElement) {
    styleElement = document.createElement('style');
    styleElement.id = styleId;
    document.head.appendChild(styleElement);
  }
  
  const css = generateTenantCSS(tenant);
  styleElement.innerHTML = css;
  
  if (process.env.NODE_ENV === 'development') {
    console.log(`✅ CSS aplicado imediatamente para ${subdomain}`);
  }
}

export type TenantConfig = {
  id: string;
  nome: string;
  slug: string;
  email: string;
  telefone?: string | null;
  cnpj?: string | null;
  status: string;
  plano: string;

  // ✅ Biografia da imobiliária
  bio?: string | null;

  // Campos de endereço
  endereco_rua?: string | null;
  endereco_numero?: string | null;
  endereco_complemento?: string | null;
  endereco_bairro?: string | null;
  endereco_cidade?: string | null;
  endereco_estado?: string | null;
  endereco_cep?: string | null;

  // Configurações da empresa
  max_imoveis?: number | null;
  max_usuarios?: number | null;

  // ✅ NOVO: Multi-Branding (Tenant Primário + Aliases)
  parent_tenant_id?: string | null;
  is_brand_alias?: boolean;
  brand_type?: string | null;

  // Dados jurídicos e redes sociais
  razao_social?: string | null;
  creci_juridico?: string | null;
  facebook_url?: string | null;
  instagram_url?: string | null;
  youtube_url?: string | null;

  // Campos para compatibilidade
  domain?: string | null;
  subdomain?: string | null;
  subscription_plan?: string | null;
  subscription_status?: string | null;
  trial_ends_at?: Date | null;
  is_active?: boolean | null;
  created_at?: Date | null;
  updated_at?: Date | null;

  // ✅ Configurações de personalização (via tenant_configurations)
  configurations?: {
    logo_url?: string | null;
    favicon_url?: string | null;
    og_image_url?: string | null;

    // ✅ Painel das telas de auth do corretor (ver TenantAuthLayout)
    login_image_url?: string | null;
    login_headline?: string | null;
    login_subline?: string | null;

    cor_primaria?: string | null;
    cor_secundaria?: string | null;
    cor_destaque?: string | null;
    cor_fundo?: string | null;
    cor_texto?: string | null;

    // ✅ Novas cores para personalização avançada
    cor_nome_primaria?: string | null;
    cor_nome_secundaria?: string | null;
    cor_titulo_destaque?: string | null;
    cor_fundo_footer?: string | null; // ✅ Nova cor para fundo do footer

    custom_css?: string | null;
    seo_title?: string | null;
    seo_description?: string | null;
    website_url?: string | null;
    support_email?: string | null;

    // ✅ Analytics e tracking
    gtm_id?: string | null;
    gtm_enabled?: boolean | null;
    analytics_config?: any | null;
    fb_pixel_id?: string | null;
  };
};

export type TenantContextValue = {
  tenant: TenantConfig | null;
  setTenant: (tenant: TenantConfig | null) => void;
  isLoading: boolean;
  error: string | null;
  refreshTenant: () => Promise<void>;
  subdomain: string;
};

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

type ProviderProps = {
  children: React.ReactNode;
  subdomain: string; // ✅ OBRIGATÓRIO: subdomain específico da tenant
  initialTenant?: TenantConfig | null;
};

export function TenantProvider({
  children,
  subdomain,
  initialTenant = null
}: ProviderProps) {
  // ✅ Sistema de cache global já funciona sem singleton

  // 🔒 CONTROLE DE INICIALIZAÇÃO
  const isInitializing = useRef(false);
  const initialTenantRef = useRef(initialTenant);

  // ✅ Estados locais para este provider específico
  const [tenant, setTenant] = useState<TenantConfig | null>(initialTenant);
  const [isLoading, setIsLoading] = useState(!initialTenant);
  const [error, setError] = useState<string | null>(null);

  // 🚀 OTIMIZAÇÃO: Cache mais agressivo para velocidade
  const isGlobalCacheValid = useCallback((cacheEntry: any) => {
    if (!cacheEntry || !cacheEntry.timestamp) return false;
    const now = Date.now();
    const cacheAge = now - cacheEntry.timestamp;
    const maxAge = 60 * 60 * 1000; // 60 minutos (cache mais longo)
    return cacheAge < maxAge;
  }, []);

  // 🔒 REMOÇÃO DO SISTEMA DE NOTIFICAÇÃO GLOBAL (causa vazamento)
  // Sistema de subscribers removido para evitar compartilhamento entre tenants

  // ✅ NOVO: Memoizar subdomain para evitar re-renders
  const stableSubdomain = useMemo(() => subdomain, [subdomain]);

  // 🔒 CONTROLE DE PRIMEIRA EXECUÇÃO
  const hasExecutedInit = useRef(false);

  // 🔒 INICIALIZAÇÃO SEGURA E ISOLADA (Declarar ANTES do useEffect)
  const initializeTenantDirectly = useCallback(async () => {
    if (typeof window === 'undefined') return;
    if (!stableSubdomain) return;

    const cache = getTenantCache();
    const existingEntry = cache.get(stableSubdomain);

    // 🔒 Evitar múltiplas requisições simultâneas para o mesmo tenant
    if (existingEntry?.loading) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`⏳ TenantProvider: Carregamento já em andamento para ${stableSubdomain}`);
      }
      return;
    }

    // 🔒 Se já tem dados válidos, usar cache
    if (existingEntry && isGlobalCacheValid(existingEntry) && existingEntry.data) {
      setTenant(existingEntry.data);
      setIsLoading(false);
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`🔒 TenantProvider: Carregando dados isolados para ${stableSubdomain}`);
    }

    try {
      setIsLoading(true);
      setError(null);

      // 🔒 Marcar como carregando no cache específico
      cache.set(stableSubdomain, {
        data: null,
        timestamp: Date.now(),
        loading: true
      });

      const response = await fetch(`/api/v1/tenants/by-subdomain/${stableSubdomain}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Falha ao carregar configurações do tenant: ${response.status}`);
      }

      const tenantData = await response.json();

      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ TenantProvider: Dados carregados para ${stableSubdomain}:`, {
          nome: tenantData.nome,
          id: tenantData.id
        });
      }

      // 🔒 ISOLAMENTO: Salvar apenas no cache específico do tenant
      cache.set(stableSubdomain, {
        data: tenantData,
        timestamp: Date.now(),
        loading: false
      });

      setTenant(tenantData);

      // 🎯 APLICAR CSS IMEDIATAMENTE quando dados chegam
      if (tenantData && tenantData.configurations) {
        // Limpar CSS anterior primeiro
        clearAllTenantCSS();
        // Aplicar novo CSS de forma síncrona
        applyTenantCSS(tenantData, stableSubdomain);
        if (process.env.NODE_ENV === 'development') {
          console.log(`⚡ CSS aplicado IMEDIATAMENTE para ${stableSubdomain} no carregamento dos dados`);
        }
      }

    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(`❌ Erro ao carregar ${stableSubdomain}:`, err);
      }
      setError(err instanceof Error ? err.message : 'Erro desconhecido');

      // 🔒 Limpar flag de carregamento em caso de erro
      cache.delete(stableSubdomain);
    } finally {
      setIsLoading(false);
    }
  }, [stableSubdomain, isGlobalCacheValid]); // ✅ CORRIGIDO: Usar stableSubdomain

  // 🚀 INICIALIZAÇÃO OTIMIZADA COM REDIS CACHE
  useEffect(() => {
    // ✅ CRÍTICO: Executar apenas uma vez por subdomain
    if (!stableSubdomain || isInitializing.current || hasExecutedInit.current) return;

    hasExecutedInit.current = true;

    const cache = getTenantCache();

    // ✅ Se initialTenant foi passado pelo servidor, pré-popular cache e não buscar
    if (initialTenantRef.current && !cache.get(stableSubdomain)?.data) {
      cache.set(stableSubdomain, {
        data: initialTenantRef.current,
        timestamp: Date.now(),
        loading: false
      });
      if (initialTenantRef.current.configurations) {
        clearAllTenantCSS();
        applyTenantCSS(initialTenantRef.current, stableSubdomain);
      }
      return;
    }

    const cacheEntry = cache.get(stableSubdomain);

    // ✅ REDIS CACHE: Verificar cache em memória primeiro
    if (cacheEntry && isGlobalCacheValid(cacheEntry) && cacheEntry.data && !cacheEntry.loading) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`⚡ TenantProvider: Cache HIT para ${stableSubdomain} - SUPER RÁPIDO!`);
      }
      setTenant(cacheEntry.data);
      setIsLoading(false);

      // 🎯 APLICAR CSS IMEDIATAMENTE quando dados vêm do cache
      if (cacheEntry.data && cacheEntry.data.configurations) {
        clearAllTenantCSS();
        applyTenantCSS(cacheEntry.data, stableSubdomain);
        if (process.env.NODE_ENV === 'development') {
          console.log(`⚡ CSS aplicado INSTANTANEAMENTE do CACHE para ${stableSubdomain}`);
        }
      }
    } else {
      // 🚀 OTIMIZAÇÃO: Inicialização não-bloqueante com Redis
      isInitializing.current = true;

      // Tentar carregar dados básicos primeiro (Redis -> DB)
      initializeTenantDirectly()
        .then(() => {
          if (process.env.NODE_ENV === 'development') {
            console.log(`⚡ Tenant ${stableSubdomain} carregado com REDIS CACHE + OTIMIZAÇÕES`);
          }
        })
        .finally(() => {
          isInitializing.current = false;
        });
    }
  }, [stableSubdomain, isGlobalCacheValid, initializeTenantDirectly]); // ✅ CORRIGIDO: Todas as dependências

  // 🔒 REMOÇÃO DO TIMEOUT: Inicialização imediata e segura
  // (timeout removido para evitar vazamento de dados entre tenants)

  // 🔒 FUNÇÃO DE BUSCA ISOLADA POR TENANT
  const fetchTenantConfig = useCallback(async () => {
    if (!subdomain) {
      throw new Error('Subdomain não fornecido');
    }

    const cache = getTenantCache();
    const cacheEntry = cache.get(subdomain);
    
    // 🔒 Usar cache apenas do tenant específico
    if (cacheEntry && isGlobalCacheValid(cacheEntry) && cacheEntry.data && !cacheEntry.loading) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ TenantProvider: Usando cache isolado para ${subdomain}`);
      }
      setTenant(cacheEntry.data);
      
      // 🎯 APLICAR CSS IMEDIATAMENTE do cache (fetchTenantConfig)
      if (cacheEntry.data && cacheEntry.data.configurations) {
        clearAllTenantCSS();
        applyTenantCSS(cacheEntry.data, subdomain);
        if (process.env.NODE_ENV === 'development') {
          console.log(`⚡ CSS aplicado do CACHE em fetchTenantConfig para ${subdomain}`);
        }
      }
      return;
    }

    // 🔒 Evitar requisições simultâneas para o mesmo tenant
    if (cacheEntry?.loading) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`⏳ TenantProvider: Aguardando carregamento para ${subdomain}`);
      }
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // 🔒 Marcar como carregando
      cache.set(subdomain, {
        data: null,
        timestamp: Date.now(),
        loading: true
      });

      if (process.env.NODE_ENV === 'development') {
        console.log(`🔒 TenantProvider: Carregando configurações isoladas para ${subdomain}`);
      }

      const response = await fetch(`/api/v1/tenants/by-subdomain/${subdomain}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Falha ao carregar configurações do tenant: ${response.status}`);
      }

      const tenantData = await response.json();
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ TenantProvider: Configurações carregadas para ${subdomain}:`, {
          nome: tenantData.nome,
          id: tenantData.id,
          logo: tenantData?.configurations?.logo_url || 'NULL'
        });
      }
      
      // 🔒 ISOLAMENTO: Salvar apenas no cache específico
      cache.set(subdomain, {
        data: tenantData,
        timestamp: Date.now(),
        loading: false
      });
      
      setTenant(tenantData);
      
      // 🎯 APLICAR CSS IMEDIATAMENTE quando dados chegam (fetchTenantConfig)
      if (tenantData && tenantData.configurations) {
        // Limpar CSS anterior primeiro
        clearAllTenantCSS();
        // Aplicar novo CSS de forma síncrona
        applyTenantCSS(tenantData, subdomain);
        if (process.env.NODE_ENV === 'development') {
          console.log(`⚡ CSS aplicado IMEDIATAMENTE para ${subdomain} via fetchTenantConfig`);
        }
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(`❌ Erro ao carregar tenant ${subdomain}:`, err);
      }
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      
      // 🔒 Limpar em caso de erro
      cache.delete(subdomain);
    } finally {
      setIsLoading(false);
    }
  }, [subdomain, isGlobalCacheValid]);

  // 🔒 REFRESH ISOLADO DO TENANT
  const refreshTenant = useCallback(async () => {
    // 🔒 Limpar cache E CSS específicos do tenant
    clearTenantCache(subdomain);
    clearTenantCSS(subdomain);
    await fetchTenantConfig();
  }, [fetchTenantConfig, subdomain]);

  // 🔧 CORREÇÃO: CSS Dinâmico com cleanup agressivo para evitar bleeding entre tenants
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    
    // 🗑️ PRIMEIRO: Sempre limpar TODO CSS de tenants para reset completo
    clearAllTenantCSS();
    
    // ✅ SEGUNDO: Aplicar CSS apenas quando tenant está completamente carregado
    if (tenant && tenant.configurations && tenant.nome && !isLoading) {
      // Aguardar um tick para garantir que a DOM está limpa
      requestAnimationFrame(() => {
        applyTenantCSS(tenant, subdomain);
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`🎯 CSS tenant aplicado (bleeding prevention) para ${subdomain}:`, {
            primary: tenant.configurations?.cor_primaria,
            secondary: tenant.configurations?.cor_secundaria,
            logo: tenant.configurations?.logo_url ? '✅' : '❌',
            nome: tenant.nome
          });
        }
      });
    }
    
    // 🧹 Cleanup: Sempre limpar quando desmonta ou muda
    return () => {
      clearTenantCSS(subdomain);
    };
  }, [subdomain, tenant?.id, tenant?.nome, isLoading]); // Dependências otimizadas para evitar re-aplicação desnecessária

  // ✅ NOVO: Context value otimizado
  const contextValue = useMemo(() => ({
    tenant,
    setTenant,
    isLoading,
    error,
    refreshTenant,
    subdomain
  }), [tenant, setTenant, isLoading, error, refreshTenant, subdomain]);

  // 🔒 LOG SEGURO: Apenas informações essenciais
  if (process.env.NODE_ENV === 'development' && tenant) {
    console.log(`🔒 TenantProvider: Tenant ${subdomain} carregado:`, {
      nome: tenant.nome,
      id: tenant.id?.substring(0, 8) + '...',
      hasConfig: !!tenant.configurations
    });
  }

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenantContext() {
  const context = useContext(TenantContext);
  
  if (context === undefined) {
    throw new Error('useTenantContext deve ser usado dentro de um TenantProvider');
  }

  // ✅ REMOVIDO: Log excessivo que causava re-renders
  // console.log('🚀 useTenantContext: Hook executado!', {
  //   timestamp: new Date().toISOString(),
  //   hasTenant: !!context.tenant,
  //   tenantId: context.tenant?.id || 'N/A'
  // });

  return context;
}

// Hook simplificado para usar apenas o tenant
export function useTenant() {
  const { tenant, isLoading, error } = useTenantContext();
  return { tenant, isLoading, error };
}


